import styled from '@emotion/styled';
import * as carriersIcon from '~/assets/icons/icon_carrierparcel.svg';
import * as customDimsIcon from '~/assets/icons/icon_customdims.svg';
import FormError from '~/components/FormError/FormError';
import BackButton from '~/components/ParcelSelect/BackButton';
import CustomParcelTemplate from '~/components/ParcelSelect/CustomParcelTemplate';
import SelectButtonContent from '~/components/ParcelSelect/SelectButtonContent/SelectButtonContent';
import SelectButton from '~/components/SelectButton';
import { GreenSpinner } from '~/components/Spinner';
import { carrierHashMap } from '~/constants/carriers';
import { InputSize } from '~/constants/form';
import { Keys } from '~/constants/keys';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { WrappedFieldProps } from 'redux-form';
import { colors } from '~/styles/constants';
import Carrier from '~/types/Carrier';
import CarrierParcelTemplate from '~/types/CarrierParcelTemplate';
import ParcelTemplate, { getDimensionsString } from '~/types/ParcelTemplate';
import { trackingHelper } from '~/utils/TrackingHelper';
import { PACKAGE_TRACKING_EVENT, TrackingAppLocation } from '~/utils/TrackingHelper/types';
import { trackPackageUpdate } from '~/utils/TrackingHelper/utils';
import Dropdown from '../Dropdown';
import ListItem from './ListItem';
import SectionTitle from './SectionTitle';

interface WrapperProps {
  size: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onKeyDown: (evt: any) => void;
  tabIndex: number;
}

const Container = styled('div')`
  display: flex;
  align-items: center;
  width: 100%;
`;

const GreenSpinnerWithMargin = styled(GreenSpinner)`
  margin-left: 5px;
`;

const Wrapper = styled('div')<WrapperProps>`
  width: 100%;
  overflow: auto;
  max-height: 260px;
  position: relative;
  background-color: ${colors.white};
`;

interface ListSectionProps {
  size: number;
  isExpanded: boolean;
}

const ListSection = styled('section')<ListSectionProps>`
  width: 100%;
  margin: 0;
  padding: 5px 0 0 0;
  box-sizing: border-box;
  height: ${(props) => (props.isExpanded ? '500px' : 'auto')};
`;

const List = styled('ul')`
  margin: 0;
  padding: 0;
`;

const ListIcon = styled('img')`
  padding-right: 12px;
  vertical-align: middle;
  padding-bottom: 2px;
`;

const FormErrorWithMarginTop = styled(FormError)`
  margin-top: 5px;
  font-size: 12px;
  color: ${colors.dangerRed};
`;

type CustomSelectButtonProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customSelectButtonStyle?: any;
  invalid?: boolean;
};

const CustomSelectButton = styled(SelectButton)<CustomSelectButtonProps>`
  color: ${colors.fontColor};
  ${({ customSelectButtonStyle }) => customSelectButtonStyle};
  ${(props) => (props.invalid ? `border: 1px solid ${colors.dangerRed}` : '')};
`;

export interface ParcelSelectStateProps {
  parcelTemplates: readonly ParcelTemplate[];
  carrierParcelTemplates: Readonly<{ [key: string]: CarrierParcelTemplate[] }>;
  isLoadingParcels: boolean;
  showZeroState?: boolean;
}

export interface ParcelSelectDispatchProps {
  // eslint-disable-next-line @typescript-eslint/ban-types
  openSavePackageTemplateModal: () => {};
}

type ParcelSelectStoreProps = ParcelSelectStateProps & ParcelSelectDispatchProps;

interface ParcelSelectProps extends ParcelSelectStoreProps {
  className?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customSelectButtonStyle?: any;
  size?: InputSize;
  selectedParcel?: ParcelTemplate;
  onChange?(parcel: Maybe<ParcelTemplate | CarrierParcelTemplate>): void;
  onCustomDimensionsSelected?(): void;
  isUsingCustomDimensions?: boolean;
  disableCustomDimensions?: boolean;
  invalid?: boolean;
  tabIndex?: number;
  omitCustomParcelTemplate?: boolean;
  disabled?: boolean;
  initialContentString: string;
  dataAnalyticsContext?: string;
  appLocation: TrackingAppLocation;
}

interface ParcelSelectState {
  isExpanded: boolean;
  isDropdownOpen: boolean;
  highlightedOptionIndex: number;
}

class ParcelSelect extends React.Component<ParcelSelectProps, ParcelSelectState> {
  static defaultProps: Partial<ParcelSelectProps> = {
    size: InputSize.medlarge,
  };

  constructor(props: ParcelSelectProps) {
    super(props);

    this.state = {
      isExpanded: false,
      isDropdownOpen: false,
      highlightedOptionIndex: 0,
    };
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  triggerRef: any = null;

  setTriggerRef = (node: React.ReactNode) => (this.triggerRef = node);

  handleClickOutside = (evt) => {
    // If the element clicked outside is inside trigger, let the trigger on click toggle
    if (evt.propagationPath().indexOf(ReactDOM.findDOMNode(this.triggerRef)) !== -1) return;

    this.setState({
      isExpanded: false,
      isDropdownOpen: false,
    });
  };

  handleSelectButtonClick = () => {
    if (this.props.isLoadingParcels) return;

    if (this.props.disabled) return;

    this.setState({
      isDropdownOpen: !this.state.isDropdownOpen,
    });
    const trackingReference = trackingHelper.getTrackingReference(
      PACKAGE_TRACKING_EVENT.name,
      this.props.appLocation,
      {
        parcelId: undefined,
        isAutoSave: false, // < -- no auto save in old parcel select
        isQuickSelect: false, // < -- No quick select in old parcel select
      },
    );

    trackPackageUpdate(trackingReference, PACKAGE_TRACKING_EVENT.operations.BROWSED, {
      parcelId: undefined,
    });
  };

  handleOptionSelection = (option: ParcelTemplate | CarrierParcelTemplate) => {
    const { onChange } = this.props;
    const parcelOption = option as ParcelTemplate;

    if (onChange) {
      onChange(option);

      if (parcelOption?.objectId) {
        const trackingReference = trackingHelper.getTrackingReference(
          PACKAGE_TRACKING_EVENT.name,
          this.props.appLocation,
          {
            parcelId: parcelOption?.objectId,
            isAutoSave: false, // < -- no auto save in old parcel select
            isQuickSelect: false, // < -- No quick select in old parcel select
          },
        );

        trackPackageUpdate(trackingReference, PACKAGE_TRACKING_EVENT.operations.ADDED, {
          parcelId: parcelOption?.objectId,
        });
      }
    }

    this.setState({
      isDropdownOpen: false,
      isExpanded: false,
    });
  };

  goBack = () => {
    this.setState({
      isExpanded: false,
    });
  };

  handleCustomDimensionsSelected = () => {
    const { onCustomDimensionsSelected, onChange } = this.props;

    if (onCustomDimensionsSelected) {
      onCustomDimensionsSelected();
    }

    if (onChange) {
      onChange(null);
    }

    this.setState({
      isDropdownOpen: false,
    });
  };

  expandList = (evt: React.MouseEvent) => {
    evt.stopPropagation();
    evt.preventDefault();

    this.setState({
      isExpanded: true,
    });
  };

  onKeyDown = (evt: React.KeyboardEvent) => {
    switch (evt.key) {
      case Keys.Escape:
      case Keys.Tab:
        // Close and reset
        return this.setState({
          isDropdownOpen: false,
          highlightedOptionIndex: 0,
          isExpanded: false,
        });
    }
  };

  renderOptions = () => {
    if (this.state.isExpanded) return null;

    const {
      omitCustomParcelTemplate,
      openSavePackageTemplateModal,
      parcelTemplates,
      showZeroState,
      dataAnalyticsContext,
    } = this.props;

    return (
      <ListSection isExpanded={this.state.isExpanded} size={this.props.size!}>
        {!this.props.disableCustomDimensions && (
          <React.Fragment>
            <SectionTitle>New package</SectionTitle>
            <List>
              <ListItem
                onClick={this.handleCustomDimensionsSelected}
                dataPendo="package-select-custom"
                dataAnalytics={
                  dataAnalyticsContext &&
                  `${dataAnalyticsContext}CustomDimensions-click-selectOption`
                }
              >
                <ListIcon src={customDimsIcon} alt="" /> Enter Custom Dimensions
              </ListItem>
              <ListItem
                onClick={this.expandList}
                dataAnalytics={
                  dataAnalyticsContext && `${dataAnalyticsContext}Carrier-click-selectOption`
                }
              >
                <ListIcon src={carriersIcon} alt="" /> Carrier Provided Parcels
              </ListItem>
            </List>
          </React.Fragment>
        )}
        {!omitCustomParcelTemplate ? (
          <CustomParcelTemplate
            onOptionSelection={this.handleOptionSelection}
            openSavePackageTemplateModal={openSavePackageTemplateModal}
            parcelTemplates={parcelTemplates}
            showZeroState={showZeroState}
            dataAnalytics={
              dataAnalyticsContext && `${dataAnalyticsContext}PackageTemplate-click-selectOption`
            }
          />
        ) : null}
      </ListSection>
    );
  };

  renderCarrierParcelTemplateItem = (
    carrier: Carrier,
    carrierParcelTemplate: CarrierParcelTemplate,
  ) => {
    const { dataAnalyticsContext } = this.props;

    return (
      <ListItem
        icon={carrier.imgTrademark ? carrier.imgTrademark : carrier.img}
        isCarrier
        subtitle={getDimensionsString(carrierParcelTemplate)}
        onClick={this.handleOptionSelection.bind(this, carrierParcelTemplate)}
        key={carrierParcelTemplate.token}
        dataPendo="package-select-carrier"
        dataAnalytics={dataAnalyticsContext && `${dataAnalyticsContext}Carrier-click-selectOption`}
      >
        {carrierParcelTemplate.name}
      </ListItem>
    );
  };

  renderCarrierParcelTemplate = (
    carrier: Carrier,
    carrierParcelTemplates: CarrierParcelTemplate[],
  ) => {
    return (
      <div key={carrier.id}>
        <SectionTitle>{carrier.name}</SectionTitle>
        <List>
          {carrierParcelTemplates.map((parcelTemplate) =>
            this.renderCarrierParcelTemplateItem(carrier, parcelTemplate),
          )}
        </List>
      </div>
    );
  };

  renderCarrierParcelTemplates = () => {
    return Object.keys(this.props.carrierParcelTemplates).map((key) => {
      // Fallback if the carrier is not mapped
      const carrier: Carrier = carrierHashMap[key] || {
        name: key,
        id: key,
        token: key,
        img: '',
      };

      return this.renderCarrierParcelTemplate(carrier, this.props.carrierParcelTemplates[key]);
    });
  };

  renderExpandedOptions = () => {
    if (!this.state.isExpanded) return null;

    return (
      <ListSection isExpanded={this.state.isExpanded} size={this.props.size!}>
        <BackButton onClick={this.goBack} />
        {this.renderCarrierParcelTemplates()}
      </ListSection>
    );
  };

  render() {
    const { isDropdownOpen } = this.state;
    const {
      customSelectButtonStyle,
      size,
      selectedParcel,
      isUsingCustomDimensions: isUsingCustomDimensions,
      tabIndex,
      className,
      disabled,
      isLoadingParcels,
      initialContentString,
    } = this.props;

    const loader = (
      <div className="flex items-center">
        <GreenSpinnerWithMargin />
      </div>
    );

    const triggerComponentContent = (
      <SelectButtonContent
        selectedParcel={selectedParcel}
        isUsingCustomDimensions={isUsingCustomDimensions}
        initialContentString={initialContentString}
        hideIcon
        hideDimensions
      />
    );
    const triggerComponent = () => (
      <CustomSelectButton
        customSelectButtonStyle={customSelectButtonStyle}
        invalid={this.props.invalid!}
        hasOptionSelected={!!selectedParcel}
        onClick={this.handleSelectButtonClick}
        size={size}
        tabIndex={tabIndex}
        disabled={isLoadingParcels || disabled}
        responsive
      >
        {isLoadingParcels ? loader : triggerComponentContent}
      </CustomSelectButton>
    );

    return (
      <Container className={className} onKeyDown={this.onKeyDown}>
        <Dropdown
          size={size}
          triggerComponent={triggerComponent}
          isOpen={isDropdownOpen}
          onClickOutside={this.handleClickOutside}
          ref={this.setTriggerRef}
        >
          <Wrapper size={size!} onKeyDown={this.onKeyDown} tabIndex={0}>
            {this.renderOptions()}
            {this.renderExpandedOptions()}
          </Wrapper>
        </Dropdown>
      </Container>
    );
  }
}

export const ParcelSelectField = (props: ParcelSelectProps & WrappedFieldProps) => {
  const { input, meta, ...parcelSelectProps } = props;
  const errors =
    meta.touched && meta.error ? (
      <FormErrorWithMarginTop>{meta.error}</FormErrorWithMarginTop>
    ) : null;

  return (
    <React.Fragment>
      <ParcelSelect
        selectedParcel={input.value}
        onChange={input.onChange}
        invalid={!!errors}
        {...parcelSelectProps}
      />
      {errors}
    </React.Fragment>
  );
};

export default ParcelSelect;
